import type {
  Block, Cell, ChipCell, CmpBlock, CmpItem, Column, KvItem, Tone,
} from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'

import type { ScreenData, ScreenLoader } from './provenance'
import {
  amountCell, asArray, asRecord, attempt, disclosure, knows, malformed, moneyCell,
  moneyReadable, readBool, readDay, readDuration, readInstant, readInt, readText,
  receipt, refLine, spanOf, summarise,
  type Attempt,
} from './wire'

/**
 * Ledger & Reconciliation — the discrepancy queue, suspense, and the
 * rebuildability check (go-wallet#752, #774, #825; this console #147).
 *
 *   lg-disc      GET /v1/admin/reconciliation/discrepancies
 *                GET /v1/admin/reconciliation/discrepancies/{id}   (row opener)
 *   lg-suspense  GET /v1/admin/ledger/suspense-lines
 *   lg-replay    GET /v1/admin/ledger/replay-runs
 *                GET /v1/admin/ledger/replay-runs/{id}             (row opener)
 *
 * # These three were recorded as having no backend, and all three do
 *
 * `SCREEN_LOADERS` said `lg-disc` and `lg-suspense` "have no table at all" and
 * `lg-replay` "is Phase 2". That was true when it was written and stopped being
 * true; the comment is corrected in the same change, because it is the sentence
 * a future reader trusts instead of re-checking.
 *
 * # NOTHING HERE CORRECTS ANYTHING, AND THAT IS THE SECTION'S WHOLE POSTURE
 *
 * The journal is append-only and the institution's record wins every
 * discrepancy — by a NEW ops-attributed posting, never by an edit. So there is
 * no resolve, no close, no acknowledge, no clear-suspense and no repair on any
 * of these three, and the corpus's resolution composer on `lg-disc` is not
 * rendered at all rather than rendered disabled: there is no route behind it.
 *
 * `lg-disc` is the sharpest instance of that rule this console has met. The
 * case carries a stored `proposal` — the compensating posting's legs, its
 * amount with currency, its reason code, all computed and persisted — and
 * NOTHING IN THE BACKEND POSTS IT. It is not "no route" and not "a route that
 * does not dispose"; it is a disposal fully specified with no executor. The
 * backend attaches a note to every response saying so, because a screen headed
 * "proposal" with a figure on it is exactly where somebody assumes a button
 * ran, and this console renders that note rather than the composer.
 *
 * # What the design draws that these reads do not send
 *
 * `lg-disc` — the STATE column (`open` / `resolved` / `closed`) is dropped:
 * `discrepancy_cases` has no status column, deliberately and with an
 * append-only trigger, because a mutable status "would then be able to disagree
 * with the journal". Every case is open. The SLA bar and the Owner column have
 * no fields at all. And the four classes are not four: only D3 writes cases
 * today — D1 writes none BY DESIGN, D2's findings are JSONB on the run rather
 * than rows, and D4 has no writer — so a screen implying four classes would
 * report three silences as cleanliness.
 *
 * `lg-suspense` — the TOTAL is dropped. Suspense legs carry their own
 * currencies and a sum across them is not a figure (invariant 4): two legs in
 * two currencies are two problems, and one number over them is neither.
 *
 * `lg-replay` — the corpus invents the table's shape. ONLY DISAGREEMENTS ARE
 * RECORDED: the accounts that agreed are not enumerated, not counted and carry
 * no per-account event total, so there is no "accounts replayed" figure and no
 * `identical` row anywhere in the response. A run with no divergences means
 * every account agreed, which is the strongest thing the check says and the
 * only form in which it says it.
 *
 * # Law 4 is not engaged here, and that is worth stating
 *
 * No screen in this module renders an available balance. What these three show
 * is a journal position, a divergence and a queue — none of which is an
 * availability figure, so there is no `min()` to draw and no `bal` block. That
 * is asserted by a test rather than left to be noticed, because law 4 is the
 * law most likely to be assumed relevant on a ledger tranche.
 *
 * # Law 12
 *
 * All three reads are Postgres. Nothing here is the analytics mirror, so no
 * block carries an analytics badge or a mirror freshness stamp.
 */

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

export const DISCREPANCIES_PATH = '/reconciliation/discrepancies'
export const SUSPENSE_PATH = '/ledger/suspense-lines'
export const REPLAY_RUNS_PATH = '/ledger/replay-runs'

export function discrepancyCasePath(id: string): string {
  return `${DISCREPANCIES_PATH}/${encodeURIComponent(id)}`
}

export function replayRunPath(id: string): string {
  return `${REPLAY_RUNS_PATH}/${encodeURIComponent(id)}`
}

const DISC_SOURCE = 'postgres · discrepancy_cases (GET /v1/admin/reconciliation/discrepancies)'
const SUSPENSE_SOURCE = 'postgres · ledger_legs (GET /v1/admin/ledger/suspense-lines)'
const REPLAY_SOURCE = 'postgres · ledger_replay_runs (GET /v1/admin/ledger/replay-runs)'

/**
 * Ours is the operational record; theirs wins.
 *
 * Said in the badge rather than folded into `operational`, because on these
 * three screens the whole subject is the two disagreeing — and invariant 2 puts
 * the institution's record above ours every time.
 */
const RECON_AUTHORITY =
  'operational — and the institution’s record wins every discrepancy, by a new posting'
/** Our own journal against our own projection. No third party is involved. */
const REPLAY_AUTHORITY = 'operational — our journal against our own projection'

// ---------------------------------------------------------------------------
// Composition helpers shared by the three screens
// ---------------------------------------------------------------------------

function unread(
  kicker: string, title: string, body: string, failed: Extract<Attempt, { ok: false }>,
  source: string,
): ScreenData {
  return {
    blocks: [{
      t: 'note', tone: 'r', kicker, title,
      body: `${failed.reason} ${body}`,
      ref: refLine(failed, 'Nothing on this screen was read.'),
    }],
    provenance: { kind: 'unread', source, reason: failed.reason },
  }
}

// ===========================================================================
// lg-disc — the discrepancy queue
// ===========================================================================

/**
 * The classes that can appear, and what each one is.
 *
 * Only D3 writes cases today. The other three are kept in the table so a class
 * this console has been taught about renders with its meaning if it ever
 * arrives, and so the screen can say — accurately — which ones cannot.
 */
const CLASS_MEANING: Readonly<Record<string, string>> = {
  D1: 'provider event missing from our ledger',
  D2: 'our posting with no provider counterpart',
  D3: 'amount, currency or reference mismatch',
  D4: 'provider adjustment or reversal',
}

/**
 * Tone by class.
 *
 * D3 is attention rather than critical. The queue is a work list and every row
 * in it is open by construction — red on all of them would be red everywhere,
 * which teaches an operator that red means nothing. Critical is kept for the
 * row whose currencies differ, where no delta can be computed at all.
 */
const CLASS_TONE: Readonly<Record<string, Tone>> = { D1: 'n', D2: 'a', D3: 'a', D4: 'a' }

/**
 * The columns.
 *
 * The corpus draws Case / Class / What it is / Customer / Amount / SLA / Owner
 * / State. Three of those have no field on this wire and one of them —
 * `State` — must not be synthesised: there is no status column and every case
 * is open.
 *
 * `Ours` and `Theirs` are two columns because they are two records with two
 * currencies, and the whole point of a D3 case is that they differ. Folding
 * them into one "Amount" is what the design did and it is what loses the
 * finding.
 */
const CASE_COLS: readonly Column[] = [
  ['Case', 'm', '92px'],
  ['Class', 'c', '104px'],
  ['What differs', 'x', 'minmax(210px,1.5fr)'],
  ['Customer', 'm', '120px'],
  ['Ours', 'a', '146px'],
  ['Theirs', 'a', '146px'],
  ['Delta', 'a', '146px'],
  ['Institution', 'c', '124px'],
  ['First seen', 'm', '150px'],
  ['Found by', 'x', '150px'],
]

interface CaseRead {
  readonly cells: readonly Cell[]
  readonly gaps: readonly string[]
  readonly id: string | null
  readonly uncomparableDelta: boolean
}

/**
 * The customer cell, and the absence that is two facts.
 *
 * An absent `customer_id` means either a posting that names nobody — an
 * operator-side movement, a fee sweep between two of our own accounts — or a
 * case predating the column. Only the second is a gap, neither is backfilled,
 * and a blank cell renders identically for both. So the cell says it cannot
 * tell, rather than picking one.
 */
function caseCustomer(o: Record<string, unknown>): Cell {
  const id = readInt(o, 'customer_id')
  if (id.kind === 'exact') return String(id.value)
  if (id.kind === 'unreadable') return '— id unreadable'
  return '— nobody, or unrecorded'
}

function readCase(raw: unknown): CaseRead | null {
  const o = asRecord(raw)
  if (o === null) return null
  const gaps: string[] = []

  const id = readInt(o, 'id')
  if (id.kind !== 'exact') gaps.push('a case id')

  const klass = readText(o, 'class')
  if (klass === null) gaps.push('a case class')
  const classCell: ChipCell = klass === null
    ? ['unstated', 'a']
    : [klass, knows(CLASS_TONE, klass) ? (CLASS_TONE[klass] ?? 'a') : 'a']
  if (klass !== null && !knows(CLASS_TONE, klass)) gaps.push('a case class this console recognises')

  // The kind is what decides where the operator goes, and it is more specific
  // than the class. `uncomparable_reason` IS the entire content of the
  // uncomparable kind — dropping it leaves a row that says something was
  // skipped and looks like coverage.
  const kind = readText(o, 'kind')
  if (kind === null) gaps.push('the kind of disagreement')
  const uncomparable = readText(o, 'uncomparable_reason')
  const defect = readText(o, 'reference_defect')
  const meaning = klass !== null && knows(CLASS_MEANING, klass) ? CLASS_MEANING[klass] ?? '' : ''
  const what = [kind ?? meaning, uncomparable, defect === null ? null : `reference ${defect}`]
    .filter((s): s is string => s !== null && s !== '')
    .join(' · ')

  // Each side with its OWN currency. Theirs is never given a currency inherited
  // from ours, because on a currency mismatch the two differing IS the finding.
  // A side with no figure carries none: a `mismatch_state` case has no `theirs`
  // at all, and a zero there would read as "they hold nothing for this", which
  // is a figure rather than an absence.
  const ours = asRecord(o['ours']) === null ? '— none on this case' : moneyCell(o, 'ours')
  const theirs = asRecord(o['theirs']) === null
    ? '— the institution states none'
    : moneyCell(o, 'theirs')
  if (asRecord(o['ours']) !== null && !moneyReadable(o, 'ours')) gaps.push('our side of a case')
  if (asRecord(o['theirs']) !== null && !moneyReadable(o, 'theirs')) gaps.push('the institution’s side of a case')

  // ABSENT, never zero and never converted, when the currencies differ. The
  // schema refuses to store one otherwise, and a substituted zero would put a
  // confident "no difference" on the case least likely to be caught any other
  // way.
  const deltaPresent = asRecord(o['delta']) !== null
  const delta = deltaPresent
    ? moneyCell(o, 'delta')
    : '— not computable across two currencies'
  if (deltaPresent && !moneyReadable(o, 'delta')) gaps.push('a delta')

  // `declined` and `reversed` are both TERMINAL, which is what tells an
  // operator not to wait for a settlement that will never come.
  const institution = readText(o, 'institution_state')
  const institutionCell: ChipCell = institution === null
    ? ['—', 'n']
    : [`${institution} · terminal`, 'a']

  const detected = readInstant(o, 'detected_at')
  if (detected.kind !== 'at') gaps.push('when a case was first seen')
  const detectedBy = readText(o, 'detected_by')
  const runID = readInt(o, 'detected_by_run_id')

  return {
    cells: [
      id.kind === 'exact' ? String(id.value) : '— no id',
      classCell,
      what === '' ? '— not stated' : what,
      caseCustomer(o),
      ours,
      theirs,
      delta,
      institutionCell,
      detected.kind === 'at' ? detected.label : '—',
      detectedBy === null
        ? '— not stated'
        : runID.kind === 'exact' ? `${detectedBy} · run ${String(runID.value)}` : detectedBy,
    ],
    gaps,
    id: id.kind === 'exact' ? String(id.value) : null,
    uncomparableDelta: !deltaPresent,
  }
}

const CASE_MISSING: readonly string[] = [
  'a case state — there is no status column and there deliberately is not one: a case is closed by a resolving ops-attributed posting that cites it, and that writer is not built, so every case here is open',
  'an SLA timer and an owner — neither has a field on this read',
  'the customer’s name — a case carries a customer id, and an absent one is either a posting that names nobody or a case older than the column, which this read cannot tell apart',
]

export function composeDiscrepancyCases(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Discrepancy cases', 'The case queue could not be read',
      'No case below is known. That is NOT evidence that our records and the institution’s agree.',
      read, DISC_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['cases'])
  if (o === null || list === null) {
    return unread(
      'Discrepancy cases', 'The case queue could not be read',
      'The queue returned a shape this console does not know.',
      malformed('The queue returned a shape this console does not know.'), DISC_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  const rowKeys: (string | null)[] = []
  let unreadable = 0
  let uncomparable = 0
  const classes = new Set<string>()

  for (const raw of list) {
    const row = readCase(raw)
    if (row === null) { unreadable += 1; continue }
    rows.push(row.cells)
    rowKeys.push(row.id)
    gaps.push(...row.gaps)
    if (row.uncomparableDelta) uncomparable += 1
    const record = asRecord(raw)
    const klass = record === null ? null : readText(record, 'class')
    if (klass !== null) classes.add(klass)
  }

  const generated = readInstant(o, 'generated_at')

  // An empty queue is NOT evidence that the records agree: a sweep that never
  // ran, or one that failed before it compared anything, also opens no cases.
  // The backend's own sentence is preferred where it sent one.
  const emptyNote = readText(o, 'empty_note')
  const footer = rows.length === 0
    ? (unreadable === 0
        ? (emptyNote ?? 'No case matches this filter. That is not evidence the records agree — a sweep that never ran opens no cases either.')
        : `No case could be read: ${String(unreadable)} came back in a shape this console does not know.`)
    : `${String(rows.length)} ${rows.length === 1 ? 'case' : 'cases'}, every one open${unreadable === 0 ? '' : ` · ${String(unreadable)} row(s) could not be read`}`

  const blocks: Block[] = [{
    t: 'table',
    title: 'Open cases',
    src: 'ledger',
    // The corpus draws class, owner and SLA facets as applied. This read
    // applies none, and a facet bar is a claim that one is applied.
    facets: false,
    cols: CASE_COLS,
    rows,
    footer,
  }]

  // Which classes can appear at all. A screen implying four would report three
  // silences as cleanliness, and this is the sentence that stops it.
  if (rows.length > 0 || emptyNote !== null) {
    blocks.push({
      t: 'note', tone: 'a', kicker: 'Coverage',
      title: 'Not every discrepancy class opens a case',
      body: `This queue holds the classes that write cases${classes.size === 0 ? '' : ` — ${[...classes].sort().join(', ')} here`}. A class that opens none is silent on this screen, and silence is not agreement.`,
      ref: 'D1 writes no case by design; D2’s findings are on the run; D4 has no writer',
    })
  }

  // The absent-customer ambiguity, in the backend's own words.
  const customerNote = disclosure(o, 'customer_note', 'Customer', 'An absent customer is two facts', 'n')
  if (customerNote !== null && rows.length > 0) blocks.push(customerNote)

  if (uncomparable > 0) {
    blocks.push({
      t: 'note', tone: 'r', kicker: 'Currency',
      title: `${String(uncomparable)} case${uncomparable === 1 ? ' has' : 's have'} no computable delta`,
      body: 'The two sides are in different currencies, so no difference exists to state. A zero here would read as agreement on the case hardest to catch any other way.',
      ref: 'delta absent · never zero, never converted',
    })
  }

  const missing = [...CASE_MISSING, ...summarise(gaps)]
  if (unreadable > 0) missing.push(`${String(unreadable)} case row(s) this console could not read`)

  return {
    blocks,
    provenance: {
      kind: 'partial', source: DISC_SOURCE, authority: RECON_AUTHORITY,
      readAt: generated.kind === 'at'
        ? `${generated.label} (the backend’s own generated_at)`
        : receipt(at),
      missing,
    },
    title: 'Discrepancy cases',
    subject: `discrepancy_cases · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
    rowKeys,
  }
}

export const loadDiscrepancyCases: ScreenLoader = async (api) =>
  composeDiscrepancyCases(
    await attempt(() => api.get<unknown>(DISCREPANCIES_PATH), 'The discrepancy queue'),
    new Date(),
  )

// ---------------------------------------------------------------------------
// One case — the row opener, with both records side by side
// ---------------------------------------------------------------------------

/**
 * Both records, as the design asks for them — and the design is right here.
 *
 * A D3 case IS the two sides disagreeing, and a table row cannot show that: it
 * has one Amount column and the case has two figures with two currencies. The
 * `cmp` block is the one place in this language that draws ours against theirs
 * with the verdict spelled out, which is exactly §6.4's requirement that a D3
 * case show "both records side by side" and label which figure the customer is
 * seeing.
 */
function bothRecords(o: Record<string, unknown>, gaps: string[]): CmpBlock {
  const ourItems: CmpItem[] = []
  const theirItems: CmpItem[] = []

  const ours = asRecord(o['ours'])
  const theirs = asRecord(o['theirs'])

  const amountOf = (side: Record<string, unknown> | null): string => {
    if (side === null) return '— none stated'
    const cell = amountCell(side['amount_minor'], side['currency'])
    return Array.isArray(cell) && typeof cell[0] === 'number' && typeof cell[1] === 'string'
      ? `${String(cell[0])} minor · ${cell[1]}`
      : typeof cell === 'string' ? cell : '—'
  }

  ourItems.push(['amount', amountOf(ours), '', 'm'])
  theirItems.push(['amount', amountOf(theirs), theirs === null ? '' : 'g', 'm'])

  // Both spellings verbatim, so which side was cut is readable from the
  // evidence. Whitespace is significant — a padded reference is invisible
  // unless the raw string is rendered.
  const oursRef = readText(o, 'ours_reference')
  const theirsRef = readText(o, 'theirs_reference')
  ourItems.push(['reference', oursRef === null ? '— none' : JSON.stringify(oursRef), '', 'm'])
  theirItems.push(['reference', theirsRef === null ? '— none' : JSON.stringify(theirsRef), '', 'm'])

  const posting = readInt(o, 'posting_id')
  if (posting.kind !== 'exact') gaps.push('the posting a case names')
  ourItems.push(['posting', posting.kind === 'exact' ? String(posting.value) : '—', '', 'm'])

  const eventID = readInt(o, 'provider_event_id')
  theirItems.push([
    'event',
    // Absent is what makes a case a reference mismatch: our reference resolved
    // to no mirror row at all.
    eventID.kind === 'exact' ? String(eventID.value) : '— our reference resolved to no event',
    eventID.kind === 'exact' ? '' : 'a',
    'm',
  ])

  const institution = readText(o, 'institution_state')
  if (institution !== null) theirItems.push(['state', `${institution} · terminal`, 'a', ''])

  return {
    t: 'cmp',
    title: 'Both records, side by side',
    sides: [
      {
        label: 'Our ledger',
        src: 'Postgres · operationally authoritative',
        tone: 'n', edge: 'a',
        meta: posting.kind === 'exact' ? `posting ${String(posting.value)}` : '',
        items: ourItems,
      },
      {
        label: `${readText(o, 'provider') ?? 'The institution'}’s record`,
        src: 'Provider · legally authoritative',
        tone: 'g', edge: 'g',
        meta: readText(o, 'provider_ref') ?? '',
        items: theirItems,
      },
    ],
    // §6.4: label which figure the customer is seeing. The institution's record
    // wins every discrepancy, so it is theirs — and saying so on the record is
    // what stops an operator quoting ours to a customer.
    verdict: theirs === null
      ? 'The institution states no amount for this movement, so there is no confirmed figure to show the customer.'
      : 'The institution’s figure is the one that governs, and the one the customer is shown.',
  }
}

export function composeDiscrepancyCase(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Discrepancy case', 'This case could not be read',
      'Nothing about it is known — including whether it exists.',
      read, DISC_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  if (o === null) {
    return unread(
      'Discrepancy case', 'This case could not be read',
      'The case returned a shape this console does not know.',
      malformed('The case returned a shape this console does not know.'), DISC_SOURCE,
    )
  }

  const gaps: string[] = []
  const blocks: Block[] = [bothRecords(o, gaps)]

  // Where this kind of disagreement is answered, beside the case rather than in
  // a runbook nobody opens at 03:00.
  const remedy = readText(o, 'remedy')
  const kv: KvItem[] = [
    ['Case', readInt(o, 'id').kind === 'exact' ? String((readInt(o, 'id') as { value: number }).value) : '— no id', '', 'n', '', 'm'],
    ['Class', readText(o, 'class') ?? '— unstated', '', 'n', '', ''],
    ['Kind', readText(o, 'kind') ?? '— unstated', '', 'n', '', 'm'],
    ['Where it is answered', remedy ?? '— this build does not know this kind', '', remedy === null ? 'a' : 'n', '', ''],
  ]
  blocks.push({ t: 'kv', title: 'The case', src: 'ledger', items: kv })

  // The proposal, and the sentence that stops it reading as an action. The
  // figures are NOT re-rendered as a composer: there is no route that posts
  // them, and a composer with a dead button is a control this backend must not
  // expose.
  const proposalNote = disclosure(
    o, 'proposal_note', 'Proposal', 'A proposal, and nothing has acted on it', 'a',
  )
  if (proposalNote !== null) blocks.push(proposalNote)

  return {
    blocks,
    provenance: {
      kind: 'partial', source: DISC_SOURCE, authority: RECON_AUTHORITY,
      readAt: receipt(at),
      missing: [
        'a resolution control — the compensating posting this case proposes has no writer anywhere in the backend, so the arithmetic is shown and nothing offers to run it',
        ...summarise(gaps),
      ],
    },
    title: `Discrepancy case ${readText(o, 'class') ?? ''}`.trim(),
    subject: 'discrepancy_cases · 1 row',
  }
}

/** One case and both records, opened from a queue row. */
export async function readDiscrepancyCase(api: ApiClient, id: string): Promise<ScreenData> {
  return composeDiscrepancyCase(
    await attempt(() => api.get<unknown>(discrepancyCasePath(id)), 'The case'),
    new Date(),
  )
}

// ===========================================================================
// lg-suspense — the clearance queue, which clears nothing
// ===========================================================================

/**
 * The columns.
 *
 * The corpus draws Leg / Posted / Case / Class / Counter-account / Amount / Age
 * / Clearing. The `Clearing` column's values — `awaiting resolution`,
 * `composer prepared`, `awaiting provider` — are a workflow vocabulary nothing
 * stores, so it is replaced by the one state that exists and by whether a case
 * has triaged the line at all.
 */
const SUSPENSE_COLS: readonly Column[] = [
  ['Leg', 'm', '104px'],
  ['Posted', 'm', '150px'],
  ['Value date', 'm', '116px'],
  ['Case', 'm', '96px'],
  ['Against', 'm', 'minmax(170px,1.2fr)'],
  ['Amount', 'a', '150px'],
  ['Age', 'n', '100px'],
  ['State', 'c', '124px'],
]

interface SuspenseRead {
  readonly cells: readonly Cell[]
  readonly gaps: readonly string[]
  readonly currency: string | null
  readonly untriaged: boolean
}

function readSuspenseLine(raw: unknown): SuspenseRead | null {
  const o = asRecord(raw)
  if (o === null) return null
  const gaps: string[] = []

  const leg = readInt(o, 'leg_id')
  if (leg.kind !== 'exact') gaps.push('a leg id')
  const posting = readInt(o, 'posting_id')

  const posted = readInstant(o, 'posted_at')
  if (posted.kind !== 'at') gaps.push('when a leg was posted')

  // ABSENT — not 1970 and not today — when the leg carries none. An
  // operator-side adjustment has no institution date, and substituting ours
  // would book it to the wrong day.
  const valueDate = readDay(o, 'value_date')

  // An absent case is a real state: a suspense leg nothing has triaged, which
  // is worth seeing rather than filtered away.
  const caseID = readInt(o, 'case_id')
  const untriaged = caseID.kind !== 'exact'

  const counterClass = readText(o, 'counter_class')
  const counterScope = readText(o, 'counter_scope')
  if (counterClass === null) gaps.push('the account a suspense entry is against')

  if (!moneyReadable(o, 'amount')) gaps.push('a suspense amount')
  const money = asRecord(o['amount'])
  const currency = money === null ? null : readText(money, 'currency')

  const age = readDuration(o, 'age_seconds')
  if (age.kind !== 'exact') gaps.push('how long a line has been open')

  // Always `open` today, and RENDERED FROM AN ABSENCE rather than from a field.
  // A line leaves suspense through a posting that names an actor and a reason
  // code; that writer is unbuilt, so no line can yet be anything else.
  const state = readText(o, 'state')
  if (state === null) gaps.push('a line state')

  return {
    cells: [
      leg.kind === 'exact' ? String(leg.value) : '— no id',
      posted.kind === 'at' ? posted.label : '—',
      valueDate ?? '— none on this leg',
      caseID.kind === 'exact' ? String(caseID.value) : '— not triaged',
      counterClass === null
        ? '— not stated'
        : counterScope === null ? counterClass : `${counterClass} · ${counterScope}`,
      moneyCell(o, 'amount'),
      age.kind === 'exact' ? spanOf(age.value) : '—',
      state === null
        ? ['unstated', 'a']
        : [state, 'a'],
    ],
    gaps,
    currency: currency === null ? null : currency.toUpperCase(),
    untriaged: untriaged && posting.kind === 'exact',
  }
}

const SUSPENSE_MISSING: readonly string[] = [
  'a total across the queue — suspense legs carry their own currencies and a sum across them is not a figure; two legs in two currencies are two problems and one number over them is neither',
  'a clearing workflow — the designed “awaiting resolution”, “composer prepared” and “awaiting provider” states are a vocabulary nothing stores; every line is open until a posting clears it',
]

export function composeSuspenseLines(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Suspense clearance', 'The suspense queue could not be read',
      'No line below is known — that is different from suspense being clear.',
      read, SUSPENSE_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['lines'])
  if (o === null || list === null) {
    return unread(
      'Suspense clearance', 'The suspense queue could not be read',
      'The queue returned a shape this console does not know.',
      malformed('The queue returned a shape this console does not know.'), SUSPENSE_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  const currencies = new Set<string>()
  let unreadable = 0
  let untriaged = 0
  for (const raw of list) {
    const row = readSuspenseLine(raw)
    if (row === null) { unreadable += 1; continue }
    rows.push(row.cells)
    gaps.push(...row.gaps)
    if (row.currency !== null) currencies.add(row.currency)
    if (row.untriaged) untriaged += 1
  }

  const accounts = readInt(o, 'open_suspense_accounts')
  const hasMore = readBool(o, 'has_more') === true

  // A count of lines and a count of currencies — never a sum. The one figure
  // this screen may not compute is the one the design asks for.
  const spread = currencies.size === 0
    ? ''
    : currencies.size === 1
      ? ` · all in ${[...currencies][0] ?? ''}`
      : ` · across ${String(currencies.size)} currencies, which is why there is no total`
  const footer = rows.length === 0
    ? (unreadable === 0
        ? 'No open suspense line. Lines arrive here from a discrepancy case and leave only through an attributed correction.'
        : `No line could be read: ${String(unreadable)} came back in a shape this console does not know.`)
    : `${String(rows.length)} open ${rows.length === 1 ? 'line' : 'lines'}${spread}${accounts.kind === 'exact' ? ` · ${String(accounts.value)} suspense account(s)` : ''}${hasMore ? ' · the backend caps the page' : ''}`

  const blocks: Block[] = [{
    t: 'table', title: 'Open suspense lines', src: 'ledger', facets: false,
    cols: SUSPENSE_COLS, rows, footer,
  }]

  // Why every state reads `open`, in the backend's own words.
  const stateNote = disclosure(o, 'state_note', 'State', 'Why every line reads open', 'n')
  if (stateNote !== null && rows.length > 0) blocks.push(stateNote)

  // And why there is no total, in the backend's own words — this is the figure
  // the design asks for and the one that must not be computed.
  const totalNote = disclosure(o, 'total_note', 'Total', 'There is no total here', 'n')
  if (totalNote !== null && rows.length > 0) blocks.push(totalNote)

  if (untriaged > 0) {
    blocks.push({
      t: 'note', tone: 'a', kicker: 'Untriaged',
      title: `${String(untriaged)} line${untriaged === 1 ? '' : 's'} no case has picked up`,
      body: 'Value is sitting in suspense with no discrepancy case naming it, so nothing is investigating them and nothing will clear them.',
      ref: 'case_id absent',
    })
  }

  const missing = [...SUSPENSE_MISSING, ...summarise(gaps)]
  if (unreadable > 0) missing.push(`${String(unreadable)} suspense line(s) this console could not read`)

  return {
    blocks,
    provenance: {
      kind: 'partial', source: SUSPENSE_SOURCE, authority: RECON_AUTHORITY,
      readAt: receipt(at), missing,
    },
    title: 'Suspense clearance',
    subject: `ledger_legs · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
  }
}

export const loadSuspenseLines: ScreenLoader = async (api) =>
  composeSuspenseLines(
    await attempt(() => api.get<unknown>(SUSPENSE_PATH), 'The suspense queue'),
    new Date(),
  )

// ===========================================================================
// lg-replay — the rebuildability check
// ===========================================================================

/**
 * Freshness, SERVED rather than derived.
 *
 * Two clients deriving one judgement is two chances to derive it differently,
 * and this judgement is the difference between "the book rebuilds" and "nobody
 * has checked since July".
 *
 * `never_run` is attention rather than critical: nothing is broken, and nothing
 * whatever is known either. Rendering it red would make the screen shout on
 * every deployment that has not scheduled the job yet, which is how an operator
 * learns to ignore it — but it is emphatically NOT green, and the footer says
 * so in words rather than relying on a colour.
 */
const FRESHNESS_TONE: Readonly<Record<string, Tone>> = {
  current: 'g',
  stale: 'a',
  never_run: 'a',
}

const FRESHNESS_MEANING: Readonly<Record<string, string>> = {
  current: 'a rebuild finished inside the window',
  stale: 'the newest rebuild predates the window, and a week of postings has landed since',
  never_run: 'no rebuild has ever finished here, so nothing at all is known about whether this ledger reconstructs',
}

/**
 * Run outcomes. `failed` is NOT a kind of clean and must not collapse into it:
 * a rebuild that timed out has established nothing about the ledger.
 */
const RUN_TONE: Readonly<Record<string, Tone>> = {
  running: 'n',
  clean: 'g',
  divergent: 'r',
  failed: 'a',
}

const RUN_COLS: readonly Column[] = [
  ['Run', 'm', '92px'],
  ['Started', 'm', '150px'],
  ['Outcome', 'c', '124px'],
  ['Divergent accounts', 'n', '156px'],
  ['Duration', 'x', '116px'],
  ['Cell', 'm', '130px'],
  ['Failure', 'x', 'minmax(190px,1.4fr)'],
]

const REPLAY_MISSING: readonly string[] = [
  'an “accounts replayed” figure and any identical row — only disagreements are recorded, so the accounts that agreed are not enumerated, not counted and carry no per-account event total',
  'a per-account events-replayed count — the rebuild folds the whole journal through a stream and reports only where the total differs',
  'a repair control — a divergence is answered with a posting made by a person holding the institution’s answer, never by a button here',
]

export function composeReplayRuns(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Replay verification', 'The rebuild history could not be read',
      'Nothing below is known. An unread history is not a passing report.',
      read, REPLAY_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['runs'])
  if (o === null || list === null) {
    return unread(
      'Replay verification', 'The rebuild history could not be read',
      'The history returned a shape this console does not know.',
      malformed('The history returned a shape this console does not know.'), REPLAY_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  const rowKeys: (string | null)[] = []
  let unreadable = 0

  for (const raw of list) {
    const r = asRecord(raw)
    if (r === null) { unreadable += 1; continue }

    const id = readInt(r, 'id')
    if (id.kind !== 'exact') gaps.push('a run id')
    const started = readInstant(r, 'started_at')
    if (started.kind !== 'at') gaps.push('when a run started')

    const outcome = readText(r, 'outcome')
    if (outcome === null) gaps.push('a run outcome')
    const outcomeCell: ChipCell = outcome === null
      ? ['unstated', 'a']
      : knows(RUN_TONE, outcome)
        ? [outcome, RUN_TONE[outcome] ?? 'a']
        : [outcome, 'a']
    if (outcome !== null && !knows(RUN_TONE, outcome)) gaps.push('a run outcome this console recognises')

    const divergent = readInt(r, 'divergent_accounts')
    if (divergent.kind !== 'exact') gaps.push('a divergent-account count')

    // Absent for a run still going, rather than zero.
    const duration = readDuration(r, 'duration_seconds')
    const finished = readInstant(r, 'finished_at')
    const durationCell = duration.kind === 'exact'
      ? spanOf(duration.value)
      : finished.kind === 'absent' ? 'still running' : '—'

    // Present only on a failed run. A statement timeout means the snapshot
    // ceiling bit, which is the rebuild becoming unrunnable as the journal
    // grows — worth reading rather than summarising.
    const failure = readText(r, 'failure_detail')

    rows.push([
      id.kind === 'exact' ? String(id.value) : '— no id',
      started.kind === 'at' ? started.label : '—',
      outcomeCell,
      // A divergent count on a FAILED run is not zero divergences: the run
      // established nothing, so the count is suppressed rather than drawn.
      outcome === 'failed'
        ? '— nothing established'
        : divergent.kind === 'exact' ? String(divergent.value) : '—',
      durationCell,
      readText(r, 'cell_id') ?? '—',
      failure ?? '',
    ])
    rowKeys.push(id.kind === 'exact' ? String(id.value) : null)
  }

  const freshness = readText(o, 'freshness')
  if (freshness === null) gaps.push('the freshness verdict')
  const lastFinished = readInstant(o, 'last_finished_at')

  // THE SENTENCE THIS SCREEN EXISTS FOR. An absent report is not a passing
  // report, and an empty run list must never render as a green check.
  const verdict = freshness === null
    ? 'The backend did not state whether a rebuild has run, so nothing here says the ledger reconstructs.'
    : `${freshness}: ${FRESHNESS_MEANING[freshness] ?? 'this console does not know this verdict'}.`

  const footer = rows.length === 0
    ? `No rebuild is recorded. ${verdict} An absent report is not a passing report.`
    : `${String(rows.length)} ${rows.length === 1 ? 'run' : 'runs'} · ${verdict}${lastFinished.kind === 'at' ? ` Last finished ${lastFinished.label}.` : ''}${unreadable === 0 ? '' : ` · ${String(unreadable)} row(s) could not be read`}`

  const blocks: Block[] = [{
    t: 'table', title: 'Rebuild runs', src: 'ledger', facets: false,
    cols: RUN_COLS, rows, footer,
  }]

  // The freshness verdict as a chip of its own, because it is a property of the
  // SCREEN rather than of any row, and the footer is where it would be missed.
  if (freshness !== null && freshness !== 'current') {
    blocks.push({
      t: 'note',
      tone: knows(FRESHNESS_TONE, freshness) ? (FRESHNESS_TONE[freshness] ?? 'a') : 'a',
      kicker: 'Freshness',
      title: freshness === 'never_run'
        ? 'Nothing has ever rebuilt this ledger'
        : 'The newest rebuild is older than the window',
      body: freshness === 'never_run'
        ? 'Nothing is known about whether this book reconstructs from its own postings. That is not the same as it reconstructing.'
        : 'A week of postings has landed since the last check, and none of them has been verified against the journal.',
      ref: `freshness = ${freshness}`,
    })
  }

  const absence = disclosure(o, 'absence_note', 'Reading this', 'An absent report is not a passing report', 'a')
  if (absence !== null) blocks.push(absence)

  const coverage = disclosure(o, 'coverage_note', 'Coverage', 'Only disagreements are recorded', 'n')
  if (coverage !== null && rows.length > 0) blocks.push(coverage)

  const missing = [...REPLAY_MISSING, ...summarise(gaps)]
  if (unreadable > 0) missing.push(`${String(unreadable)} run row(s) this console could not read`)

  return {
    blocks,
    provenance: {
      kind: 'partial', source: REPLAY_SOURCE, authority: REPLAY_AUTHORITY,
      readAt: receipt(at), missing,
    },
    title: 'Replay verification',
    subject: `ledger_replay_runs · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
    rowKeys,
  }
}

export const loadReplayRuns: ScreenLoader = async (api) =>
  composeReplayRuns(
    await attempt(() => api.get<unknown>(REPLAY_RUNS_PATH), 'The rebuild history'),
    new Date(),
  )

// ---------------------------------------------------------------------------
// One run's divergences — the row opener
// ---------------------------------------------------------------------------

/**
 * What each divergence kind says about the book.
 *
 * `unjustified_by_journal` is the alarming direction and it is the only one
 * drawn critical: the projection holds a position no posting accounts for,
 * which if real is value the operator issued.
 */
const DIVERGENCE_MEANING: Readonly<Record<string, readonly [string, Tone]>> = {
  amount_mismatch: ['the rebuild and the stored balance disagree on the figure', 'a'],
  missing_from_projection: ['the journal justifies a balance the projection does not hold', 'a'],
  unjustified_by_journal: ['the projection holds a position no posting accounts for', 'r'],
}

const DIVERGENCE_COLS: readonly Column[] = [
  ['Account', 'm', 'minmax(180px,1.3fr)'],
  ['Customer', 'm', '110px'],
  ['Rebuilt from the journal', 'a', '170px'],
  ['Stored', 'a', '150px'],
  ['Difference', 'a', '150px'],
  ['Kind', 'c', '190px'],
  ['What it means', 'x', 'minmax(200px,1.5fr)'],
]

export function composeReplayRun(read: Attempt, id: string, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Replay run', 'This run could not be read',
      'Nothing about it is known — including whether it found anything.',
      read, REPLAY_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  const run = o === null ? null : asRecord(o['run'])
  const list = o === null ? null : asArray(o['divergences'])
  if (o === null || run === null || list === null) {
    return unread(
      'Replay run', 'This run could not be read',
      'The run returned a shape this console does not know.',
      malformed('The run returned a shape this console does not know.'), REPLAY_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0
  let unjustified = 0

  for (const raw of list) {
    const d = asRecord(raw)
    if (d === null) { unreadable += 1; continue }

    const scope = readText(d, 'account_scope')
    const klass = readText(d, 'account_class')
    if (klass === null) gaps.push('an account class')

    const customer = readInt(d, 'customer_id')

    // Never omitted and never defaulted: every figure below is minor units of
    // THIS currency, and a figure without it is not a money figure.
    const currency = readText(d, 'currency')
    if (currency === null) gaps.push('a currency on a divergence')

    // ABSENT means the side does not exist, and the two absences are the two
    // most different findings here. Zero would say the side was computed and
    // came out flat, which is an ordinary balance.
    const rebuilt = d['rebuilt_minor']
    const stored = d['stored_minor']
    const difference = d['difference_minor']

    const kind = readText(d, 'kind')
    if (kind === null) gaps.push('a divergence kind')
    const known = kind !== null && knows(DIVERGENCE_MEANING, kind)
    const meaning = known ? DIVERGENCE_MEANING[kind] : undefined
    if (kind !== null && !known) gaps.push('a divergence kind this console recognises')
    if (kind === 'unjustified_by_journal') unjustified += 1

    rows.push([
      scope === null ? (klass ?? '— unnamed') : `${klass ?? '—'} · ${scope}`,
      customer.kind === 'exact' ? String(customer.value) : '— no customer',
      rebuilt === undefined || rebuilt === null
        ? '— the journal justifies no balance'
        : amountCell(rebuilt, currency),
      stored === undefined || stored === null
        ? '— the projection holds no balance'
        : amountCell(stored, currency),
      // Absent when one side does not exist, because subtracting from a
      // missing row produces the other side's whole balance and renders as a
      // discrepancy of that size — a smaller and more tractable problem than
      // the one actually found.
      difference === undefined || difference === null
        ? '— one side does not exist, so there is no difference to state'
        : amountCell(difference, currency),
      kind === null ? ['unstated', 'a'] : [kind, meaning?.[1] ?? 'a'],
      meaning?.[0] ?? '— this console does not know this kind',
    ])
  }

  const outcome = readText(run, 'outcome')
  const runID = readInt(run, 'id')

  // A clean run is the strongest thing this check says, and the ONLY form in
  // which it says it: no divergences means every account agreed. That sentence
  // has to be on the screen, because an empty table otherwise reads as a run
  // that did nothing.
  const footer = rows.length === 0
    ? (outcome === 'failed'
        ? 'This run failed, so it established nothing. An empty divergence list here is not a clean book.'
        : unreadable === 0
          ? 'No divergence. Every account the journal describes matches the stored balance — which is the strongest thing this check says, and the only way it says it.'
          : `No divergence could be read: ${String(unreadable)} came back in a shape this console does not know.`)
    : `${String(rows.length)} divergent ${rows.length === 1 ? 'account' : 'accounts'} · only disagreements are recorded, so the accounts that agreed are not listed`

  const blocks: Block[] = [{
    t: 'table',
    title: runID.kind === 'exact' ? `Divergences · run ${String(runID.value)}` : 'Divergences',
    src: 'ledger', facets: false,
    cols: DIVERGENCE_COLS, rows, footer,
  }]

  if (unjustified > 0) {
    blocks.push({
      t: 'note', tone: 'r', kicker: 'Unjustified',
      title: `${String(unjustified)} account${unjustified === 1 ? ' holds' : 's hold'} a position no posting accounts for`,
      body: 'The projection carries value the journal does not justify. If that is real it is value issued without a posting behind it, and it is answered by a person holding the institution’s answer.',
      ref: 'kind = unjustified_by_journal',
    })
  }

  const remedy = disclosure(o, 'remedy_note', 'Remedy', 'A divergence is answered with a posting', 'n')
  if (remedy !== null && rows.length > 0) blocks.push(remedy)

  const missing = [
    'a repair control — there is none at any permission, and no way to start a rebuild from this surface',
    ...summarise(gaps),
  ]
  if (unreadable > 0) missing.push(`${String(unreadable)} divergence(s) this console could not read`)

  return {
    blocks,
    provenance: {
      kind: 'partial', source: REPLAY_SOURCE, authority: REPLAY_AUTHORITY,
      readAt: receipt(at), missing,
    },
    title: `Replay run ${id}`,
    subject: `ledger_replay_divergences · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
  }
}

/** One run's divergences, opened from a run row. */
export async function readReplayRun(api: ApiClient, id: string): Promise<ScreenData> {
  return composeReplayRun(
    await attempt(() => api.get<unknown>(replayRunPath(id)), 'The rebuild run'),
    id,
    new Date(),
  )
}
